import {
  Box,
  Button,
  Card,
  CircularProgress,
  InputAdornment,
  OutlinedInput,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { IconSearch } from '@tabler/icons-react';
import getAxiosInstance from 'api';
import moment from 'moment';
import { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import { getDateRangeFromQuickOption } from 'utils/common';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  border: '1px solid #DEDEDE',
  color: '#212120',
  fontSize: '14px',
  fontWeight: '500',
}));

const DateRangeButton = styled(Button)(() => ({
  backgroundColor: '#fff',
  color: '#000',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  borderRadius: '8px',
  padding: '8px 16px',
  textTransform: 'none',
  fontWeight: 400,
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
}));

const OutlineInputStyle = styled(OutlinedInput)(() => ({
  width: 350,
  marginLeft: 16,
  paddingLeft: 16,
  paddingRight: 16,
  borderRadius: '8px',
  border: '1px solid rgba(33, 33, 32, 0.10)',
  background: '#FFFFFF',
  '& input': {
    background: 'transparent !important',
    paddingLeft: '4px !important',
  },
}));

const quickRanges = [
  { label: 'Current Day', value: 'day' },
  { label: 'Current Week', value: 'week' },
  { label: 'Current Month', value: 'month' },
  { label: 'Last 3 Months', value: '3months' },
  { label: 'Last 6 Months', value: '6months' },
  { label: 'Last 12 Months', value: '12months' },
  { label: 'Quarter to date', value: 'quarter' },
  { label: 'All Time', value: 'all' },
];

const headCells = [
  { id: 'email', label: 'Email Address' },
  { id: 'name', label: 'Name' },
  { id: 'partnertype', label: 'Type' },
  { id: 'product', label: 'Product' },
  { id: 'uploadType', label: 'Upload Type' },
  { id: 'createdAt', label: 'Date' },

];

const ProductType = [
  { label: 'All', value: 'all' },
  { label: 'Direct Debit', value: 'direct_debit' },
  { label: 'Income Verification', value: 'income_verification' },
  { label: 'BVN', value: 'bvn' },
  { label: 'Credit Risk', value: 'credit' },
];

const type = [
  { label: 'Online', value: 'online' },
  { label: 'API', value: 'api' },
];

function EnhancedTableHead({
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  rowCount,
  onRequestSort,
}) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const StyledTableCellHead = styled(TableCell)(({ theme }) => ({
    backgroundColor: '#F0F0F0',
    color: 'rgba(33, 33, 32, 0.60)',
    border: '1px solid #DEDEDE',
  }));

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <StyledTableCellHead
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : undefined}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order.toLowerCase() : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'DESC' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </StyledTableCellHead>
        ))}
      </TableRow>
    </TableHead>
  );
}

const TransactionHistory = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [order, setOrder] = useState('DESC');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState({
    product: ProductType[0],
    type: type[0],
  });
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedDate, setSelectedDate] = useState([
    moment().startOf('month').toDate(),
    moment().toDate(),
  ]);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [quickRangeValue, setQuickRangeValue] = useState('');

  const fetchData = useCallback(() => {
    setLoading(true);
    getAxiosInstance(
      `adminOrchestrator/dashboard/getActiveProducts`,
      'post',
      {
        startDate: moment(selectedDate[0]).format('MM-DD-YYYY'),
        endDate: moment(selectedDate[1]).format('MM-DD-YYYY'),
        page: page + 1,
        limit,
        search,
        filter: {
          product: filter.product?.value || ProductType[0].value,
          type: filter.type?.value || type[0].value,
        },
        sortBy: orderBy,
        sortDirection: order,
      },
      { 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          setCalls(res?.data?.data?.items || []);
          setTotal(res?.data?.data?.total || 0);
        } else {
          setCalls([]);
          setTotal(0);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Error fetching data');
      })
      .finally(() => setLoading(false));
  }, [selectedDate, page, limit, search, filter, orderBy, order]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setOrderBy(property);
  };

  const handleFilterChange = (key, newValue) => {
    setFilterLoading(true);
    setFilter({ ...filter, [key]: newValue || ProductType[0] });
    setTimeout(() => {
      setFilterLoading(false);
    }, 300);
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h3">Transaction History</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ width: 170 }}>
            <Select
              value={filter.product?.value || ProductType[0].value}
              onChange={(event) => {
                const selectedValue = event.target.value;
                const selectedOption = ProductType.find((p) => p.value === selectedValue) || ProductType[0];
                handleFilterChange('product', selectedOption);
              }}
              displayEmpty
              sx={{
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              }}
            >
              {ProductType.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: 170 }}>
            <Select
              value={filter.type?.value || type[0].value}
              onChange={(event) => {
                const selectedValue = event.target.value;
                const selectedOption = type.find((t) => t.value === selectedValue) || type[0];
                handleFilterChange('type', selectedOption);
              }}
              displayEmpty
              sx={{
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              }}
            >
              {type.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: 170 }}>
            <Select
              value={quickRangeValue || ''}
              onChange={(event) => {
                const newValue = event.target.value;
                setQuickRangeValue(newValue);
                if (!newValue) return;
                const range = getDateRangeFromQuickOption(newValue);
                if (!range) return;
                setSelectedDate([range.start, range.end]);
              }}
              displayEmpty
              inputProps={{ 'aria-label': 'Quick Range' }}
              sx={{
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              }}
            >
              <MenuItem value="">Quick Range</MenuItem>
              {quickRanges.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <DateRangePicker
            appearance="default"
            placeholder="Select Date Range"
            value={selectedDate}
            onChange={(value) => {
              setSelectedDate(value);
              setQuickRangeValue('');
            }}
            showOneCalendar
          />
          <OutlineInputStyle
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            startAdornment={
              <InputAdornment position="start">
                <IconSearch
                  stroke={1.5}
                  size="16px"
                  color={theme.palette.grey[500]}
                />
              </InputAdornment>
            }
          />
        </Stack>
      </Stack>

      <Card sx={{ mt: 2 }}>
        {loading ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', height: '100px' }}
          >
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table sx={{ minWidth: 750 }} size="medium">
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {calls.length ? (
                    calls.map((row, index) => (
                      <TableRow hover key={index}>
                        <StyledTableCell
                          style={{ cursor: 'pointer' }}
                          onClick={() =>
                            navigate(
                              `/business-details/${row?.businessPartnerId}`
                            )
                          }
                        >
                          {row?.email || row?.userEmail}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {row?.name ||
                            `${row?.firstName || ''} ${
                              row?.lastName || ''
                            }`.trim()}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {row?.partnertype}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {row?.product}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {row?.uploadType}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {moment(row?.createdAt).format(
                            'MMM DD, YYYY hh:mm A'
                          )}
                        </StyledTableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={total}
              rowsPerPage={limit}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) =>
                setLimit(parseInt(e.target.value, 10))
              }
            />
          </>
        )}
      </Card>
    </>
  );
};

export default TransactionHistory;
